import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertTriangle, Check } from 'lucide-react';
import { Button } from '../primitives/button';
import { useIsInsideDialogContent } from '../primitives/dialog';
import { cn } from '../lib/utils';
import {
  intentToButtonVariant,
  visibleActions,
  type ActionDefinition,
} from './action-definition';
import {
  resolveVisibleSections,
  type FormSection,
} from './form-shell-visibility';
import { errorSignature, focusFirstFormError } from './form-shell-error-focus';

export { resolveVisibleSections, type FormSection } from './form-shell-visibility';
export {
  focusFirstFormError,
  invalidFieldSelectors,
  firstErrorField,
} from './form-shell-error-focus';

export interface FormShellProps<TState = Record<string, unknown>> {
  /** Eyebrow above the form title. */
  eyebrow?: React.ReactNode;
  /** Form title — single H1 of the page. */
  title: React.ReactNode;
  /** Optional subtitle/description. */
  description?: React.ReactNode;
  /**
   * Declarative form sections with optional conditional visibility. Use this
   * instead of (or alongside) `children` when sections need to appear /
   * disappear based on other field values — pass the predicate's `state`
   * source via `formState`. Sections render before `children`.
   */
  sections?: FormSection<TState>[];
  /**
   * Current form state that `sections[].visible` and function `content`
   * predicates resolve against — typically the react-hook-form `watch()`
   * result or your form's value object.
   */
  formState?: TState;
  /** Form body — typically <FormField> entries from `@tyndo/ui-kit/primitives`. */
  children?: React.ReactNode;
  /** Errors to summarize at the top of the form. */
  errors?: Array<{ field?: string; message: string }>;
  /** Whether the form is dirty — drives the unsaved-changes guard text. */
  dirty?: boolean;
  /** Whether the form is currently submitting — disables actions, shows spinner state. */
  submitting?: boolean;
  /** Called when the user clicks the primary save action. */
  onSave?: () => void;
  /**
   * Disable the primary save action independently of `submitting`/errors —
   * e.g. when the caller lacks the permission the save endpoint requires, so
   * the button is present-but-disabled (with an optional `saveDisabledReason`
   * tooltip) rather than firing a request that will be rejected.
   */
  saveDisabled?: boolean;
  /** Native title on the save button when `saveDisabled` — explains why. */
  saveDisabledReason?: string;
  /** Called when the user clicks cancel. */
  onCancel?: () => void;
  /** Override default action labels. */
  saveLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** Optional data-testid for the primary save button (for e2e selectors). */
  saveTestId?: string;
  /** Optional data-testid for the cancel button (for e2e selectors). */
  cancelTestId?: string;
  /** Extra controls in the footer (e.g., "Save as draft"). */
  extraActions?: React.ReactNode;
  /**
   * Canonical footer actions (rendered between the dirty hint and
   * cancel/save). Prefer over `extraActions` in new code — same
   * `ActionDefinition` shape the other shells accept.
   */
  actions?: ActionDefinition[];
  /** Right-side metadata — last saved, status, etc. */
  metadataSlot?: React.ReactNode;
  /**
   * Register the form title as the dialog's Radix `DialogTitle` so the dialog
   * has an accessible name (no Radix missing-title error, named for assistive
   * tech). Auto-detected when FormShell renders inside a ui-kit `DialogContent`;
   * pass `false` to opt out (e.g. when the host already renders its own
   * `DialogTitle`), or `true` to force it on for a non-standard dialog host.
   */
  asDialogTitle?: boolean;
  className?: string;
}

/**
 * Tyndo FormShell — the standard scaffold for any record form.
 *
 * - Sticky action footer with save/cancel.
 * - Validation summary box at the top when errors are passed.
 * - Dirty-state hint in the footer; the host can wire a beforeunload guard.
 * - Designed to be a wrapper, not a form library — bring your own
 *   react-hook-form + zod and just hand FormShell its `errors` + `dirty` flags.
 */
export function FormShell<TState = Record<string, unknown>>({
  eyebrow,
  title,
  description,
  sections,
  formState,
  children,
  errors,
  dirty,
  submitting,
  onSave,
  saveDisabled,
  saveDisabledReason,
  onCancel,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  saveTestId,
  cancelTestId,
  extraActions,
  actions,
  metadataSlot,
  asDialogTitle,
  className,
}: FormShellProps<TState>) {
  const hasErrors = (errors?.length ?? 0) > 0;
  const footerActions = visibleActions(actions ?? []);

  // Ref onto the scroll container so error-focusing can query the rendered
  // controls (both `children` and declarative `sections`) for the offending
  // field. Kept off the outer wrapper so we don't reach into the sticky
  // footer's Save/Cancel buttons.
  const bodyRef = React.useRef<HTMLDivElement>(null);

  // When a failed submit surfaces (or changes) the error set, move focus to
  // the first invalid control so the user is taken to the problem rather than
  // left hunting — WCAG 3.3.1 / 2.4.3. Keyed on the error *contents* (not just
  // the boolean) so a second failed submit with a different first-error still
  // re-focuses. The `role="alert"` summary announces the count to AT in
  // parallel; this handles the keyboard/pointer focus move.
  const errorKey = errorSignature(errors);
  React.useEffect(() => {
    if (!hasErrors) return;
    // Defer to the paint after the summary + aria-invalid controls render so
    // the target control exists in the DOM when we query for it.
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(() => focusFirstFormError(bodyRef.current, errors))
        : (focusFirstFormError(bodyRef.current, errors), 0);
    return () => {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- errorKey is the stable content key for `errors`
  }, [errorKey, hasErrors]);

  // Focus a specific field when its error-summary entry is activated, so the
  // summary doubles as a jump list into the form.
  const focusField = React.useCallback((field?: string) => {
    focusFirstFormError(bodyRef.current, [{ field, message: '' }]);
  }, []);

  // When FormShell is the title-bearer inside a dialog, register its visible
  // <h1> as the Radix DialogTitle (via `asChild`, so the rendered markup and
  // styling are byte-for-byte identical) — this gives the dialog an accessible
  // name and clears Radix's missing-title error. Auto-on inside a ui-kit
  // DialogContent; `asDialogTitle` overrides the detection either way.
  const insideDialog = useIsInsideDialogContent();
  const registerAsDialogTitle = asDialogTitle ?? insideDialog;

  // Resolve conditional visibility against the current form state. A section
  // with no `visible` predicate always renders; otherwise it is skipped when
  // the predicate returns false so its inputs leave tab order + validation.
  const resolvedState = (formState ?? ({} as TState));
  const visibleSections = resolveVisibleSections(sections, resolvedState);

  return (
    <div className={cn('flex h-full flex-col bg-surface-base', className)}>
      <div ref={bodyRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-6 py-6">
          {/* Header */}
          <header className="mb-6">
            {eyebrow && <p className="t-eyebrow">{eyebrow}</p>}
            {(() => {
              const h1 = (
                <h1 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-foreground">
                  {title}
                </h1>
              );
              // `asChild` projects the dialog's titleId onto this exact <h1>,
              // so the dialog gets an accessible name without changing markup.
              return registerAsDialogTitle ? (
                <DialogPrimitive.Title asChild>{h1}</DialogPrimitive.Title>
              ) : (
                h1
              );
            })()}
            {description && (
              <p className="mt-1 text-sm text-muted-foreground">{description}</p>
            )}
          </header>

          {/* Validation summary */}
          {hasErrors && (
            <div
              role="alert"
              className="mb-5 rounded-md border border-[hsl(var(--alarm-critical)/0.3)] bg-[hsl(var(--alarm-critical)/0.06)] p-3"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-[hsl(var(--alarm-critical))] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[hsl(var(--alarm-critical))]">
                    {errors!.length} issue{errors!.length === 1 ? '' : 's'} to resolve
                  </p>
                  <ul className="mt-1 space-y-0.5 text-xs text-foreground/80">
                    {errors!.slice(0, 5).map((e, i) => (
                      <li key={i}>
                        {e.field ? (
                          // A field-scoped issue is a jump link: activating it
                          // focuses the offending control so the summary
                          // doubles as a navigable error index (WCAG 2.4.3).
                          <Button
                            type="button"
                            variant="link"
                            onClick={() => focusField(e.field)}
                            className="h-auto p-0 text-left text-xs font-normal text-foreground/80 underline-offset-2 hover:underline"
                          >
                            <span className="t-mono text-muted-foreground">
                              {e.field}:
                            </span>{' '}
                            {e.message}
                          </Button>
                        ) : (
                          e.message
                        )}
                      </li>
                    ))}
                    {errors!.length > 5 && (
                      <li className="text-muted-foreground">
                        … and {errors!.length - 5} more
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="space-y-5">
            {visibleSections.map((section) => (
              <section
                key={section.id}
                aria-labelledby={section.title ? `${section.id}-heading` : undefined}
                className="space-y-3"
              >
                {(section.title || section.description) && (
                  <div className="space-y-0.5">
                    {section.title && (
                      <h2
                        id={`${section.id}-heading`}
                        className="text-sm font-semibold tracking-tight text-foreground"
                      >
                        {section.title}
                      </h2>
                    )}
                    {section.description && (
                      <p className="text-xs text-muted-foreground">
                        {section.description}
                      </p>
                    )}
                  </div>
                )}
                {typeof section.content === 'function'
                  ? section.content(resolvedState)
                  : section.content}
              </section>
            ))}
            {children}
          </div>
        </div>
      </div>

      {/* Sticky footer */}
      {(onSave || onCancel || extraActions || footerActions.length > 0) && (
        <div className="sticky bottom-0 z-10 border-t border-border bg-surface-raised/80 backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-3">
            <div className="flex flex-1 items-center gap-3 text-xs text-muted-foreground">
              {dirty && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--alarm-minor))]" />
                  Unsaved changes
                </span>
              )}
              {!dirty && submitting === false && (
                <span className="inline-flex items-center gap-1.5 text-accent-live">
                  <Check className="h-3.5 w-3.5" />
                  All changes saved
                </span>
              )}
              {metadataSlot}
            </div>
            {footerActions.map((a) => (
              <Button
                key={a.id}
                type="button"
                size="sm"
                variant={intentToButtonVariant(a.intent)}
                onClick={a.onSelect}
                disabled={a.disabled || submitting}
              >
                {a.icon && <span className="mr-1 inline-flex">{a.icon}</span>}
                {a.label}
              </Button>
            ))}
            {extraActions}
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancel}
                disabled={submitting}
                data-testid={cancelTestId}
              >
                {cancelLabel}
              </Button>
            )}
            {onSave && (
              <Button
                type="button"
                size="sm"
                onClick={onSave}
                disabled={submitting || hasErrors || saveDisabled}
                title={saveDisabled ? saveDisabledReason : undefined}
                data-testid={saveTestId}
              >
                {submitting ? 'Saving…' : saveLabel}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
